import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Box,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import {
  Store as StoreIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import authInstance from '../../services/httpClient';
import Logger from '../../utils/Logger';

interface Store {
  id: string;
  name: string;
}

interface StoreMessage {
  id: string;
  messageType: string;
  targetStoreId: string;
  title: string;
  content: string;
  status: string;
  sendPushNotification: boolean;
  createdAt: string;
  sentAt: string;
  expiresAt: string | null;
}

const StoreSpecificMessages: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [activeMessages, setActiveMessages] = useState<StoreMessage[]>([]);
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showComposer, setShowComposer] = useState(false);

  // Form state
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [content, setContent] = useState('');
  const [sendPush, setSendPush] = useState(false);

  // Fetch stores
  const fetchStores = async () => {
    try {
      const { data } = await authInstance.get('/admin/stores');
      // API returns array directly, not wrapped in { stores: [] }
      setStores(Array.isArray(data) ? data : []);
    } catch (err: any) {
      Logger.error('Failed to fetch stores:', err);
      setError('Failed to load stores');
    }
  };

  // Fetch active store-specific messages
  const fetchActiveMessages = async () => {
    try {
      setLoading(true);
      const { data } = await authInstance.get('/admin/store-messages', {
        params: {
          messageType: 'store_specific',
          status: 'sent',
          page: 1,
          limit: 100,
        },
      });

      setActiveMessages(data.messages || []);
    } catch (err: any) {
      Logger.error('Failed to fetch store messages:', err);
      setError(err.response?.data?.message || 'Failed to load store messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
    fetchActiveMessages();
  }, []);

  const handleSendMessage = async () => {
    if (!selectedStoreId || !content.trim()) {
      setError('Store and message content are required');
      return;
    }

    try {
      setSending(true);
      setError('');
      setSuccess('');

      const storeName = stores.find((s) => s.id === selectedStoreId)?.name || 'Store';

      await authInstance.post('/admin/store-messages', {
        messageType: 'store_specific',
        targetStoreId: selectedStoreId,
        title: storeName, // Use store name as title (not displayed on mobile)
        content: content.trim(),
        sendPushNotification: sendPush,
      });

      setSuccess(`Message sent to ${storeName} users!`);
      setSelectedStoreId('');
      setContent('');
      setSendPush(false);
      setShowComposer(false);

      // Refresh messages
      setTimeout(() => {
        fetchActiveMessages();
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      Logger.error('Failed to send store message:', err);
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      setDeletingId(messageId);
      setError('');

      await authInstance.post(`/admin/store-messages/${messageId}/expire`, {});

      setSuccess('Store message closed and moved to history');
      setActiveMessages((prev) => prev.filter((m) => m.id !== messageId));

      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      Logger.error('Failed to close message:', err);
      setError(err.response?.data?.message || 'Failed to close message');
    } finally {
      setDeletingId(null);
    }
  };

  const getStoreName = (storeId: string) => {
    return stores.find((s) => s.id === storeId)?.name || 'Unknown Store';
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <StoreIcon color="primary" />
            <Typography variant="h6">🏪 Store-Specific Messages</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowComposer(!showComposer)}
          >
            {showComposer ? 'Cancel' : 'New Message'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Composer Form */}
        <Collapse in={showComposer}>
          <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" gutterBottom>
              Compose Store Message
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Store</InputLabel>
              <Select
                value={selectedStoreId}
                onChange={(e) => setSelectedStoreId(e.target.value)}
                label="Select Store"
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Message (Single line - appears as orange banner)"
              fullWidth
              value={content}
              onChange={(e) => setContent(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Enter your message here..."
              inputProps={{ maxLength: 35 }}
              helperText={`${content.length}/35 characters — keep it to 35 or it truncates in the app banner`}
            />

            <FormControlLabel
              control={<Switch checked={sendPush} onChange={(e) => setSendPush(e.target.checked)} />}
              label="📱 Also send as push notification"
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSendMessage}
              disabled={sending || !selectedStoreId || !content.trim()}
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {sending ? 'Sending...' : 'Send to Store Users'}
            </Button>
          </Box>
        </Collapse>

        {/* Active Messages List */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : activeMessages.length === 0 ? (
          <Alert severity="info">No active store-specific messages</Alert>
        ) : (
          <List>
            {activeMessages.map((message) => (
              <ListItem
                key={message.id}
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  mb: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <StoreIcon fontSize="small" color="action" />
                    <ListItemText
                      primary={getStoreName(message.targetStoreId)}
                      secondary={message.content.substring(0, 50) + (message.content.length > 50 ? '...' : '')}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpandedMessageId(expandedMessageId === message.id ? null : message.id)
                      }
                    >
                      {expandedMessageId === message.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteMessage(message.id)}
                      disabled={deletingId === message.id}
                    >
                      {deletingId === message.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Box>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expandedMessageId === message.id} sx={{ width: '100%', mt: 1 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: 'none',
                      borderRadius: 1,
                      backgroundColor: '#ff9800',
                      minHeight: '46px',
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 'semibold', flex: 1, color: 'white' }}>
                      {message.content}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Sent: {new Date(message.sentAt).toLocaleString()}
                    </Typography>
                    {message.sendPushNotification && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        📱 Push notification sent
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default StoreSpecificMessages;
